import { defineStore } from "pinia";
import router from "../router/index";
import AuthService from "@/service/AuthService";
import ProfileService from "@/service/ProfileService";
import { useCommonStore } from "@/stores";
import getTokenFromCookies from "@/helpers/getTokenFromCookies";

export const useAuthStore = defineStore({
  id: "auth",
  state: () => ({
    token: getTokenFromCookies(),
    isAuthenticated: false,
    regUserData: null,
    clickUuid: null,
    countryCode: null,
  }),
  mutations: {
    setAuth(state, status) {
      state.isAuthenticated = status;
    },
  },
  actions: {
    async login(email, password) {
      const commonStore = useCommonStore();
      commonStore.loaderOn();
      try {
        const result = await AuthService.signIn({
          email,
          password,
        });
        const token = result.data.token;
        this.token = token;

        const date = new Date();
        date.setTime(date.getTime() + 23 * 60 * 60 * 1000);

        const { protocol } = window.location;
        if (protocol === "http:") {
          document.cookie = `token=${token}; path=/; domain=.${import.meta.env.VITE_DOMAIN}; SameSite=Lax; expires=${date.toUTCString()};`;
        }
        if (protocol === "https:") {
          document.cookie = `token=${token}; path=/; domain=.${import.meta.env.VITE_DOMAIN}; SameSite=Lax; secure; expires=${date.toUTCString()};`;
        }

        this.deleteCountryCookies();

        this.isAuthenticated = true;
        this.countryCode = null;
        router.push("/home");
        return result;
      } catch (error) {
        return error.response.data;
      } finally {
        commonStore.loaderOff();
      }
    },
    async logout(page = '/login') {
      const commonStore = useCommonStore();
      try {
        commonStore.loaderOn();
        await AuthService.logoutRequest();
      } catch (error) {
        return error.response.data;
      } finally {
        commonStore.loaderOff();
        this.token = null;
        this.countryCode = null;
        this.isAuthenticated = false;

        const { protocol } = window.location;
        if (protocol === "http:") {
          document.cookie = `token=; path=/; domain=${import.meta.env.VITE_DOMAIN}; SameSite=Lax; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
        }
        if (protocol === "https:") {
          document.cookie = `token=; path=/; domain=${import.meta.env.VITE_DOMAIN}; SameSite=Lax; secure; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
        }
        router.push(page);
      }
    },
    async checkAuthStatus() {
      if (!this.token) {
        this.isAuthenticated = false;
        return false;
      }
      try {
        const { data, status } = await ProfileService.getProfileInfo();
        this.isAuthenticated = status === 200;

        if (!this.countryCode && this.isAuthenticated) {
          this.countryCode = data.country_code
        }
        return this.isAuthenticated;
      } catch (error) {
        if (error.response && error.response.status === 401) {
          await this.logout();
          router.push("/login");
        }
        this.isAuthenticated = false;
        return false;
      }
    },
    setRegUserData(data) {
      this.regUserData = data;
    },
    setclickUuid(id) {
      this.clickUuid = id;
    },
    setCountryCookies(country) {
      const { protocol } = window.location;
      if (protocol === 'http:') {
        document.cookie = `pref_country=${country}; path=/; domain=.${import.meta.env.VITE_DOMAIN}; SameSite=Lax;`;
      }
      if (protocol === 'https:') {
        document.cookie = `pref_country=${country}; path=/; domain=.${import.meta.env.VITE_DOMAIN}; SameSite=Lax; secure;`;
      }
    },
    deleteCountryCookies() {
      const { protocol } = window.location;
      if (protocol === 'http:') {
        document.cookie = `pref_country=; path=/; domain=${import.meta.env.VITE_DOMAIN}; SameSite=Lax; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
      }
      if (protocol === 'https:') {
        document.cookie = `pref_country=; path=/; domain=${import.meta.env.VITE_DOMAIN}; SameSite=Lax; secure; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
      }
    },
  },
  getters: {
    isAuth(state) {
      return state.isAuthenticated;
    },
    getAuthCountryCode(state) {
      return state.countryCode;
    }
  },
});
